<template>
  <l-modal
    :title="t('menu-availability.stock.stock-management')"
    :button-text="t('ctas.save')"
    :button-enabled="canSave"
    secondary-button
    :secondary-button-text="t('menu-availability.stock.remove')"
    @action="updateStock"
    @secondary-action="removeStock"
    @close="() => $emit('close')"
  >
    <div class="flex flex-col gap-6">
      <stock-editor
        v-model="stock"
        :low-stock-threshold="lowStockThreshold"
        :max-value="maxValue"
      />

      <restocking-mode-editor
        v-model:policy="refillPolicy"
        v-model:value="refillValue"
        :options="options"
        :options-with-refill-amount="refillPolicyOptionsWithRefillAmount"
        :max-value="maxValue"
      />

      <div class="flex flex-col gap-4">
        <div>{{ t('menu-availability.stock.alert.title') }}</div>
        <l-field :label="t('menu-availability.stock.alert.threshold-label')">
          <l-input v-model="lowStockThreshold" type="number" :min="0" :max="maxValue" />
        </l-field>
        <div class="flex justify-between bg-n-600 gap-6 p-4 rounded-lg">
          <div class="flex flex-col gap-2">
            <div class="font-bold text-sm">
              {{ t('menu-availability.stock.alert.hide-platforms.label') }}
            </div>
            <div class="text-xs">
              {{ t('menu-availability.stock.alert.hide-platforms.description') }}
            </div>
          </div>
          <l-switch class="flex-shrink-0" v-model="hideOnLowStock" />
        </div>
      </div>
    </div>
  </l-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LField, LInput, LModal, LSwitch } from '@last/core-ui/paprika'

import { useStockStore } from '@/store/stock'
import sync from '@/sync/service'
import type { StockItem } from '@/types'

import RestockingModeEditor from './RestockingModeEditor.vue'
import StockEditor from './StockEditor.vue'

const maxValue = 99999

const emit = defineEmits<{
  close: []
}>()

const { updateItemStock, removeItemStock } = useStockStore()

const props = defineProps<{ productStock: StockItem }>()

const { t } = useI18n()

const options = ['until-sold-out', 'refill-daily', 'refill-shift', 'just-today'] as const
type RefillOption = (typeof options)[number]
const refillPolicyOptionsWithRefillAmount = ['refill-daily', 'refill-shift'] as RefillOption[]

const stock = ref<number>(0)
const refillPolicy = ref<RefillOption>(options[0])
const refillValue = ref<number | undefined>(undefined)
const lowStockThreshold = ref<number | undefined>(undefined)
const hideOnLowStock = ref<boolean>(true)

onMounted(() => {
  const product = props.productStock
  stock.value = product.stock ?? 0

  const incomingPolicy = product.refillPolicy
  if (options.includes(incomingPolicy as RefillOption)) {
    refillPolicy.value = incomingPolicy as RefillOption
  } else {
    refillPolicy.value = options[0]
  }

  refillValue.value = product.refillValue
  lowStockThreshold.value = product.lowStockThreshold ?? 5
  hideOnLowStock.value = product.hideOnLowStock ?? true
})

async function removeStock() {
  removeItemStock(props.productStock.id)
  emit('close')
}

async function updateStock() {
  if (!refillPolicy.value) return

  if (stock.value > maxValue) {
    stock.value = maxValue
  }

  sync.record('setProductStock', {
    organizationProductId: props.productStock.id,
    stockInfo: {
      stock: stock.value,
      lowStockThreshold: lowStockThreshold.value
    }
  })

  const finalRefillValue = refillValue.value ?? stock.value

  await updateItemStock(
    props.productStock.id,
    stock.value,
    refillPolicy.value,
    finalRefillValue,
    lowStockThreshold.value,
    hideOnLowStock.value
  )

  emit('close')
}

const canSave = computed(() => {
  const stockIsValid = typeof stock.value === 'number' && stock.value >= 0
  const policyIsValid = !!refillPolicy.value
  return stockIsValid && policyIsValid
})
</script>
